import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SliderComponent } from '@examdojo/core/widget';
import katex from 'katex';
import { normalCdf } from '../../../shared/normal-dist-math.util';
import { NormalDistributionChartComponent } from '../normal-distribution-chart';
import { NormalDistributionChartParams } from '../normal-distribution-chart/normal-distribution-chart.model';

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html' });
}

export interface ZLabParams {
  initialMu: number;
  initialSigma: number;
  initialX: number;
  chartHeight?: number;
}

const DEFAULT_PARAMS: ZLabParams = {
  initialMu: 170,
  initialSigma: 15,
  initialX: 185,
  chartHeight: 250,
};

const BLUE = '#3b82f6';
const PURPLE = '#8b5cf6';
const BLUE_FILL = 'rgba(59, 130, 246, 0.2)';
const PURPLE_FILL = 'rgba(139, 92, 246, 0.2)';
const ORANGE = '\\color{#f97316}';
const INDIGO = '\\color{#6366f1}';

@Component({
  selector: 'dojo-z-lab',
  standalone: true,
  imports: [NormalDistributionChartComponent, SliderComponent],
  templateUrl: './z-lab.component.html',
  styleUrl: './z-lab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class ZLabComponent {
  constructor() {
    effect(
      () => {
        const p = this.params();
        this.mu.set(p.initialMu);
        this.sigma.set(p.initialSigma);
        this.x.set(p.initialX);
      },
      { allowSignalWrites: true },
    );
  }

  private readonly sanitizer = inject(DomSanitizer);

  readonly params = input<ZLabParams>(DEFAULT_PARAMS);

  readonly mu = signal(DEFAULT_PARAMS.initialMu);
  readonly sigma = signal(DEFAULT_PARAMS.initialSigma);
  readonly x = signal(DEFAULT_PARAMS.initialX);

  readonly z = computed(() => (this.x() - this.mu()) / this.sigma());
  readonly prob = computed(() => normalCdf(this.z()));

  readonly chartHeight = computed(() => this.params().chartHeight ?? 250);
  readonly sliderXMin = computed(() => Math.floor(this.mu() - 4 * this.sigma()));
  readonly sliderXMax = computed(() => Math.ceil(this.mu() + 4 * this.sigma()));

  readonly xTitleHtml = computed<SafeHtml>(() => {
    const muVal = fmt(this.mu());
    const sigmaVal = fmt(this.sigma());
    const latex = `X \\sim N({${ORANGE}{${muVal}}},\\, {${INDIGO}{${sigmaVal}}}^2)`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly zTitleHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(renderLatex('Z \\sim N(0, 1)'));

  readonly xChartParams = computed<NormalDistributionChartParams>(() => {
    const mu = this.mu();
    const sigma = this.sigma();
    const xVal = this.x();
    const xMin = Math.floor(mu - 4 * sigma);
    const xMax = Math.ceil(mu + 4 * sigma);

    return {
      curves: [{ mu, sigma, color: BLUE }],
      xMin,
      xMax,
      gridRight: 20,
      shadedRegions: [{ from: xMin, to: xVal, color: BLUE_FILL }],
      topLabels: [{ value: xVal, text: `x = ${fmt(xVal)}`, color: BLUE }],
    };
  });

  readonly zChartParams = computed<NormalDistributionChartParams>(() => {
    const z = this.z();
    const zClamped = Math.max(-4, Math.min(4, z));

    return {
      curves: [{ mu: 0, sigma: 1, color: PURPLE }],
      xMin: -4,
      xMax: 4,
      gridRight: 20,
      shadedRegions: [{ from: -4, to: zClamped, color: PURPLE_FILL }],
      topLabels: [{ value: zClamped, text: `z = ${fmt(z)}`, color: PURPLE }],
    };
  });

  readonly zFormulaHtml = computed<SafeHtml>(() => {
    const xVal = fmt(this.x());
    const muVal = fmt(this.mu());
    const sigmaVal = fmt(this.sigma());
    const zVal = fmt3(this.z());
    const latex = `z = \\frac{x - ${ORANGE}{\\mu}}{${INDIGO}{\\sigma}} = \\frac{${xVal} - ${ORANGE}{${muVal}}}{${INDIGO}{${sigmaVal}}} = ${zVal}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly probFormulaHtml = computed<SafeHtml>(() => {
    const xVal = fmt(this.x());
    const zVal = fmt3(this.z());
    const probVal = fmt4(this.prob());
    const latex = `P(X \\leq ${xVal}) = P(Z \\leq ${zVal}) = ${probVal}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly muLabelHtml = this.sanitizer.bypassSecurityTrustHtml(renderLatex('\\mu'));
  readonly sigmaLabelHtml = this.sanitizer.bypassSecurityTrustHtml(renderLatex('\\sigma'));

  readonly onMuChange = (value: number): void => {
    this.mu.set(value);
  };

  readonly onSigmaChange = (value: number): void => {
    this.sigma.set(Math.max(0.5, value));
  };

  readonly onXChange = (value: number): void => {
    this.x.set(value);
  };
}

function fmt(n: number): string {
  return parseFloat(n.toFixed(2)).toString();
}

function fmt3(n: number): string {
  return parseFloat(n.toFixed(3)).toString();
}

function fmt4(n: number): string {
  return n.toFixed(4);
}
